// ============================================================================
// PQ Chat panel — Phase A: chat with PQ in context on the estimate builder.
// ----------------------------------------------------------------------------
// Sheet-style side panel (full-width on mobile — it will be used from the
// truck). Reopens the estimate's latest conversation; the `pq-chat` edge
// function assembles context server-side and persists both turns. The panel
// only renders history + sends messages — no context is built client-side.
// ============================================================================
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Loader2, Send, RotateCcw, Check, Plus, X, Paperclip, FileSpreadsheet, FileText, Bookmark, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PQShield } from '@/components/pq/PQMark';
import DraftReviewCard from '@/components/pq/DraftReviewCard';
import DraftReportCard from '@/components/pq/DraftReportCard';
import { BasisBadge } from '@/components/pq/basisTiers';
import { toast } from 'sonner';
import {
  sendPqChatMessage,
  fetchLatestConversation,
  fetchPqMemories,
  forgetPqMemory,
  fetchMessages,
  attachPhotoForChat,
  uploadSupplierSheetForChat,
  uploadDocumentForChat,
  pqChatErrorMessage,
} from '@/lib/pqChatData';

// Mirrors HISTORY_WINDOW in supabase/functions/pq-chat/index.ts — how many
// of the thread's messages actually reach the model. Kept in sync by hand;
// if it changes there, change it here.
const PQ_HISTORY_WINDOW = 20;

const KIND_LABELS = {
  scope_item: 'Scope item',
  exclusion: 'Exclusion',
  line_item: 'Line item',
  callout: 'Callout',
};

/**
 * One PQ-drafted change, awaiting the contractor's decision. Apply pushes it
 * into the open builder via onApply (nothing is written until then); Dismiss
 * hides the card for this session. Applied/dismissed state is session-local —
 * the estimate itself is the record of what was accepted.
 */
const ProposalCard = ({ proposal, state, onApply, onDismiss, canApply, t }) => {
  if (state === 'dismissed') return null;
  const applied = state === 'applied';
  const inEstimate = state === 'in_estimate';
  return (
    <div
      className="mr-auto max-w-[85%] rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 space-y-1.5"
      data-testid={`pq-proposal-${proposal.id}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-primary">
        <span>{KIND_LABELS[proposal.kind] || proposal.kind}</span>
        {proposal.section ? (
          <span className="text-muted-foreground normal-case tracking-normal">
            → {proposal.section}
          </span>
        ) : null}
      </div>
      <div className="text-sm text-foreground whitespace-pre-wrap">
        {proposal.title ? <span className="font-medium">{proposal.title}: </span> : null}
        {proposal.text}
        {proposal.kind === 'line_item' ? (
          <span className="text-muted-foreground">
            {' — '}
            {proposal.amount != null
              ? `$${Number(proposal.amount).toLocaleString()}`
              : t('pqchat.to_be_priced', { defaultValue: 'to be priced' })}
          </span>
        ) : null}
      </div>
      {/* Where the price came from — server-graded, tap to expand. */}
      {proposal.kind === 'line_item' && proposal.basis ? (
        <BasisBadge basis={proposal.basis} />
      ) : null}
      {proposal.note ? (
        <div className="text-[11px] italic text-muted-foreground whitespace-pre-wrap border-l-2 border-primary/25 pl-2">
          <span className="not-italic font-medium text-primary/80">
            {t('pqchat.internal_note', { defaultValue: 'Internal — won’t print:' })}
          </span>{' '}
          {proposal.note}
        </div>
      ) : null}
      <div className="flex items-center gap-2 pt-0.5">
        {applied || inEstimate ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-600">
            <Check size={12} />
            {applied
              ? t('pqchat.applied', { defaultValue: 'Added — hit Save to keep it' })
              : t('pqchat.in_estimate', { defaultValue: 'Already in the estimate' })}
          </span>
        ) : !canApply ? (
          <span className="text-[11px] text-muted-foreground">
            {t('pqchat.apply_elsewhere', {
              defaultValue: 'Open the estimate in the builder to apply drafts.',
            })}
          </span>
        ) : (
          <>
            <Button
              size="sm"
              onClick={() => onApply(proposal)}
              className="h-7 px-2 bg-primary text-primary-foreground hover:bg-primary/90 text-xs"
              data-testid={`pq-proposal-apply-${proposal.id}`}
            >
              <Plus size={12} className="mr-1" />
              {t('pqchat.apply', { defaultValue: 'Apply' })}
            </Button>
            <button
              type="button"
              onClick={() => onDismiss(proposal)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid={`pq-proposal-dismiss-${proposal.id}`}
            >
              <X size={12} />
              {t('pqchat.dismiss', { defaultValue: 'Dismiss' })}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// PQ writes plain text with **bold** for emphasis (prompt rule 9) — render
// just that one marker, plus bare https:// URLs as tappable links (the
// export_takeoff tool drops signed download links into replies). Everything
// else stays literal pre-wrapped text.
const linkifyParts = (text, keyBase) =>
  String(text ?? '')
    .split(/(https:\/\/[^\s<>"')\]]+)/g)
    .map((part, i) =>
      part.startsWith('https://') ? (
        <a
          key={`${keyBase}-${i}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-[hsl(var(--primary))] break-all"
        >
          {part}
        </a>
      ) : (
        <React.Fragment key={`${keyBase}-${i}`}>{part}</React.Fragment>
      )
    );

const renderInlineBold = (text) =>
  String(text ?? '')
    .split(/(\*\*[^*\n]+\*\*)/g)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={i}>{linkifyParts(part, i)}</React.Fragment>
      )
    );

const Bubble = ({ role, children }) => (
  <div
    className={
      role === 'user'
        ? 'ml-auto max-w-[85%] rounded-lg bg-primary/15 border border-primary/25 px-3 py-2 text-sm text-foreground whitespace-pre-wrap'
        : 'mr-auto max-w-[85%] rounded-lg bg-[hsl(var(--muted))] border border-border px-3 py-2 text-sm text-foreground whitespace-pre-wrap'
    }
  >
    {typeof children === 'string' ? renderInlineBold(children) : children}
  </div>
);

/**
 * Context prop: { type: 'estimate' | 'job', id, title }. Legacy estimateId /
 * estimateTitle props still work (mapped to an estimate context). One PQ,
 * many conversations — each context gets its own resumable thread.
 */
export const PQChatPanel = ({
  open,
  onOpenChange,
  context: contextProp,
  estimateId,
  estimateTitle,
  onApplyProposal,
  // (proposal) => bool — content already present in the OPEN estimate. Derived
  // truth from the document (see isProposalInEstimate); survives reloads where
  // the session-local applied/dismissed marks do not.
  isProposalApplied,
}) => {
  const { t } = useTranslation();
  const context = contextProp || {
    type: 'estimate',
    id: estimateId || null,
    title: estimateTitle || '',
  };
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  // Draft survives refreshes/remounts: a contractor lost a couple of typed
  // paragraphs to a mid-type reload (live-fire 2026-09-09). Every keystroke
  // mirrors to localStorage per context; the key clears on successful send.
  // try/catch everywhere — storage can be unavailable (private mode etc.).
  const draftKey = `pqchat-draft-${context.type || 'estimate'}-${context.id || 'none'}`;
  const [draft, setDraft] = useState(() => {
    try {
      return localStorage.getItem(draftKey) || '';
    } catch (_) {
      return '';
    }
  });
  const persistDraft = (value) => {
    try {
      if (value && value.trim()) localStorage.setItem(draftKey, value);
      else localStorage.removeItem(draftKey);
    } catch (_) {
      /* storage unavailable — typing still works, it just won't survive */
    }
  };
  const updateDraft = (valueOrFn) => {
    setDraft((prev) => {
      const next =
        typeof valueOrFn === 'function' ? valueOrFn(prev) : valueOrFn;
      persistDraft(next);
      return next;
    });
  };
  // Context switched (different estimate/job): load that context's own draft.
  useEffect(() => {
    try {
      setDraft(localStorage.getItem(draftKey) || '');
    } catch (_) {
      setDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // proposal id -> 'applied' | 'dismissed' (session-local; the estimate is
  // the durable record of what was accepted)
  const [proposalStates, setProposalStates] = useState({});
  const [attaching, setAttaching] = useState(false);
  // Attachment tray — every file the user attaches this round shows as a
  // visible chip (thumbnail for photos, sheet chip for .xlsx) with a live
  // uploading → attached/failed state. Before this, the only feedback was a
  // toast that vanished in seconds; users couldn't tell whether the attach
  // actually took (dogfood 2026-09-26). Chips clear when the next message is
  // sent — the message that references them is then in the thread.
  const [attachments, setAttachments] = useState([]);
  const attachIdRef = useRef(0);
  // Drag & drop state. The depth counter absorbs the enter/leave churn from
  // child elements so the overlay doesn't flicker while dragging across them.
  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // v10: replies are produced server-side in the background — the send returns
  // immediately and the assistant row lands in the conversation when done.
  // We poll the thread until the last message is PQ's. Because the rows
  // persist, leaving the page (or the app) doesn't lose the run: reopening
  // the chat resumes the wait, or just shows the finished answer.
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (convoId) => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const list = await fetchMessages(convoId);
        const last = list[list.length - 1];
        if (last && last.role === 'assistant') {
          stopPolling();
          setMessages(list);
          setSending(false);
          scrollToBottom();
          return;
        }
      } catch (_) {
        /* transient — keep polling */
      }
      if (ticks > 150) {
        // ~10 minutes — something's genuinely stuck
        stopPolling();
        setSending(false);
        setError(
          t('pqchat.poll_timeout', {
            defaultValue:
              "PQ is taking unusually long. Reopen this chat in a minute — the answer may still land — or send the message again.",
          })
        );
      }
    }, 4000);
  };

  useEffect(() => stopPolling, []);

  // loadThread below is memoized on the estimate's identity and must not be
  // recreated every render (its effect would refetch in a loop). It reaches
  // the current startPolling through this ref instead of a dependency.
  const startPollingRef = useRef(startPolling);
  startPollingRef.current = startPolling;

  // Snap-and-ask: photos ride the walkthrough rails (created on demand);
  // priced supplier sheets (.xlsx coming back from an RFQ) go to the
  // pq-exports bucket where PQ's import_supplier_quote tool reads them.
  // Either way the draft is seeded so the next send points PQ at it.
  // Estimate context only — job chats have no walkthrough or RFQ.
  const isSheetFile = (f) =>
    /\.xlsx$/i.test(f?.name || '') ||
    f?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  // A store "export cart as PDF" or any other non-image, non-.xlsx file used
  // to fall through to the photo path silently: it uploaded "successfully"
  // (contentType stamped from file.type) but wasn't a real image, so PQ's
  // view_walkthrough_photos handed Claude an unusable image block and the
  // reply died quietly — attach looked like it worked, PQ never responded.
  // Reject those up front with an actionable message instead.
  const isImageFile = (f) =>
    /^image\//.test(f?.type || '') ||
    /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(f?.name || '');
  // PDFs (supplier quotes, invoices, spec sheets) go to their own folder,
  // where pq-chat's read_attached_document hands them to the model whole —
  // never down the photo path, which can't read them (see above).
  const isPdfFile = (f) =>
    f?.type === 'application/pdf' || /\.pdf$/i.test(f?.name || '');
  const kindOf = (f) => (isSheetFile(f) ? 'sheet' : isPdfFile(f) ? 'pdf' : 'photo');

  const handleAttachFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (files.length === 0) return;
    if (!context.id || context.type !== 'estimate') {
      // Dropped/pasted into a chat that can't take attachments — say so
      // instead of silently eating the file.
      toast.error(
        t('pqchat.attach_estimate_only', {
          defaultValue: 'Attachments work in estimate chats — open Ask PQ from the estimate.',
        })
      );
      return;
    }
    const isSupported = (f) => isSheetFile(f) || isPdfFile(f) || isImageFile(f);
    const supported = files.filter(isSupported);
    const unsupported = files.filter((f) => !isSupported(f));
    if (unsupported.length > 0) {
      toast.error(
        t('pqchat.attach_unsupported', {
          defaultValue:
            "That file type isn't supported here — attach a photo or screenshot, a PDF, or a priced .xlsx sheet.",
        })
      );
    }
    if (supported.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // Register every file as a visible chip FIRST, then upload one by one,
    // flipping each chip's status as its upload settles. The tray is the
    // answer to "did that actually attach?" — success toasts are gone, the
    // chips are the feedback.
    const entries = supported.map((file) => ({
      id: `att-${++attachIdRef.current}`,
      file,
      name: file.name,
      kind: kindOf(file),
      status: 'uploading',
      previewUrl: kindOf(file) === 'photo' ? URL.createObjectURL(file) : null,
    }));
    setAttachments((a) => [...a, ...entries]);
    setAttaching(true);
    let sheetName = null;
    let pdfName = null;
    let photoCount = 0;
    for (const entry of entries) {
      try {
        if (entry.kind === 'sheet') {
          const res = await uploadSupplierSheetForChat(context.id, entry.file);
          sheetName = res.filename || entry.name;
        } else if (entry.kind === 'pdf') {
          const res = await uploadDocumentForChat(context.id, entry.file);
          pdfName = res.filename || entry.name;
        } else {
          await attachPhotoForChat(context.id, entry.file);
          photoCount += 1;
        }
        setAttachments((a) =>
          a.map((x) => (x.id === entry.id ? { ...x, status: 'attached' } : x))
        );
      } catch (e) {
        setAttachments((a) =>
          a.map((x) => (x.id === entry.id ? { ...x, status: 'failed' } : x))
        );
        toast.error(
          e?.message || t('pqchat.attach_failed', { defaultValue: 'Attachment upload failed.' })
        );
      }
    }
    if (sheetName) {
      updateDraft((d) => {
        const seed = t('pqchat.attach_sheet_draft', {
          defaultValue:
            'The supplier sent the priced sheet back ({{name}}) — file it into the price book. Supplier: ',
          name: sheetName,
        });
        return d.trim() ? `${d.trim()}\n${seed}` : seed;
      });
    }
    if (pdfName) {
      updateDraft((d) => {
        const seed = t('pqchat.attach_pdf_draft', {
          defaultValue: 'I attached a PDF ({{name}}) — read it and ',
          name: pdfName,
        });
        return d.trim() ? `${d.trim()}\n${seed}` : seed;
      });
    }
    if (photoCount > 0) {
      updateDraft((d) =>
        d.trim()
          ? d
          : t('pqchat.attach_draft', {
              defaultValue: 'Look at the photo I just added — ',
            })
      );
    }
    setAttaching(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag & drop anywhere on the panel. Only real file drags get the overlay;
  // text selections dragged across the panel are ignored.
  const hasFileDrag = (e) => {
    const types = e?.dataTransfer?.types;
    return !!types && Array.from(types).includes('Files');
  };
  const onDragEnter = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
  };
  const onDragLeave = () => {
    if (dragDepthRef.current > 0) dragDepthRef.current -= 1;
    if (dragDepthRef.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!hasFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    handleAttachFiles(e.dataTransfer.files);
  };

  const handleApply = (proposal) => {
    if (!onApplyProposal) return;
    const ok = onApplyProposal(proposal);
    if (ok !== false) {
      setProposalStates((s) => ({ ...s, [proposal.id]: 'applied' }));
    }
  };
  const handleDismiss = (proposal) =>
    setProposalStates((s) => ({ ...s, [proposal.id]: 'dismissed' }));

  // Takeoff volume: a 25-card takeoff shouldn't need 25 taps. Pending = every
  // proposal in the thread not yet applied/dismissed this session.
  // Drafts are excluded: a whole-document draft is a deliberate, reviewed
  // Apply of its own, not something to sweep up in a bulk action. Report
  // cards are excluded because there is nothing to apply — they are read-only.
  // Session state wins (it reflects THIS session's clicks, including on a
  // not-yet-saved document); otherwise derive from the document: content
  // already in the estimate renders as such instead of a live Apply button.
  const stateFor = (p) =>
    proposalStates[p.id] ||
    (isProposalApplied && isProposalApplied(p) ? 'in_estimate' : undefined);

  const pendingProposals = messages.flatMap((m) =>
    Array.isArray(m.proposals)
      ? m.proposals.filter(
          (p) => p.kind !== 'draft' && p.kind !== 'report' && !stateFor(p)
        )
      : []
  );
  const handleApplyAll = () => {
    if (!onApplyProposal) return;
    let applied = 0;
    const next = {};
    for (const p of pendingProposals) {
      if (onApplyProposal(p) !== false) {
        next[p.id] = 'applied';
        applied += 1;
      }
    }
    if (applied > 0) setProposalStates((s) => ({ ...s, ...next }));
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  // On open: resume the estimate's latest thread (Phase A keeps one thread
  // per estimate front and center; "new conversation" resets below).
  const loadThread = useCallback(async () => {
    if (!context.id) return;
    setLoading(true);
    setError('');
    try {
      const convo = await fetchLatestConversation(context.type, context.id);
      if (convo) {
        setConversationId(convo.id);
        const list = await fetchMessages(convo.id);
        setMessages(list);
        // A thread ending on the contractor's turn means a background run is
        // (or was) in flight — resume waiting for PQ's reply.
        const last = list[list.length - 1];
        if (last && last.role === 'user') {
          setSending(true);
          startPollingRef.current(convo.id);
        }
      } else {
        setConversationId(null);
        setMessages([]);
      }
      scrollToBottom();
    } catch (e) {
      setError(pqChatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [context.type, context.id]);

  useEffect(() => {
    if (open) loadThread();
  }, [open, loadThread]);

  // Decision memory (migration 088): what PQ remembers for this record and
  // company-wide. Shown so the contractor can SEE every remembered fact and
  // delete a wrong one — memory he can't inspect would be a black box.
  // Reloads when the panel opens and after each reply (PQ may have saved one).
  const [memory, setMemory] = useState({ available: false, rows: [] });
  const [memoryOpen, setMemoryOpen] = useState(false);
  const loadMemory = useCallback(async () => {
    try {
      const res = await fetchPqMemories({
        estimateId: context.type === 'estimate' ? context.id : null,
        jobId: context.type === 'job' ? context.id : null,
      });
      setMemory(res);
    } catch {
      setMemory({ available: false, rows: [] });
    }
  }, [context.type, context.id]);
  useEffect(() => {
    if (open) loadMemory();
  }, [open, loadMemory, messages.length]);
  const handleForget = async (row) => {
    try {
      await forgetPqMemory(row.id);
      setMemory((m) => ({ ...m, rows: m.rows.filter((r) => r.id !== row.id) }));
      toast.success(t('pqchat.memory_forgotten', { defaultValue: 'PQ forgot that.' }));
    } catch (e) {
      toast.error(e?.message || t('pqchat.memory_forget_failed', { defaultValue: 'Could not remove it.' }));
    }
  };

  const handleSend = async () => {
    const message = draft.trim();
    if (!message || sending) return;
    setSending(true);
    setError('');
    updateDraft('');
    // The message referencing the attachments is off — clear the tray and
    // release the thumbnail object URLs.
    setAttachments((a) => {
      a.forEach((x) => {
        if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
      });
      return [];
    });
    // optimistic user bubble
    setMessages((ms) => [
      ...ms,
      { id: `local-${Date.now()}`, role: 'user', content: message },
    ]);
    scrollToBottom();
    try {
      const res = await sendPqChatMessage({
        conversationId,
        estimateId: context.type === 'estimate' ? context.id : undefined,
        jobId: context.type === 'job' ? context.id : undefined,
        message,
      });
      if (res?.conversationId && res.conversationId !== conversationId) {
        setConversationId(res.conversationId);
      }
      if (res?.pending) {
        // v10 background mode: the reply lands in the thread when PQ's done —
        // poll for it. `sending` stays true so the thinking indicator shows;
        // leaving and coming back resumes the wait via loadThread.
        startPolling(res.conversationId || conversationId);
        return;
      }
      // Legacy synchronous response (pre-v10 function still deployed).
      setMessages((ms) => [
        ...ms,
        {
          id: `local-${Date.now()}-a`,
          role: 'assistant',
          content: res.reply,
          proposals: Array.isArray(res.proposals) && res.proposals.length > 0 ? res.proposals : undefined,
        },
      ]);
      scrollToBottom();
      setSending(false);
    } catch (e) {
      setError(pqChatErrorMessage(e));
      updateDraft(message); // give the text back so nothing is lost
      setMessages((ms) => ms.filter((m) => m.content !== message || m.role !== 'user'));
      setSending(false);
    }
  };

  const handleNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setError('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col bg-[hsl(var(--card))] border-border"
        data-testid="pq-chat-panel"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragOver ? (
          <div className="absolute inset-1 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-[hsl(var(--card))]/90 pointer-events-none">
            <div className="flex flex-col items-center gap-2 text-primary">
              <Paperclip size={22} />
              <span className="text-sm font-medium">
                {t('pqchat.drop_to_attach', {
                  defaultValue: 'Drop to attach — photos, PDFs, or a priced .xlsx',
                })}
              </span>
            </div>
          </div>
        ) : null}
        <SheetHeader className="px-4 pt-4 pb-2 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <PQShield size={22} />
            {t('pqchat.title', { defaultValue: 'Ask PQ' })}
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {context.title
              ? t('pqchat.subtitle_estimate', {
                  defaultValue: 'Talking about: {{name}}',
                  name: context.title,
                })
              : t('pqchat.subtitle_general', {
                  defaultValue: 'PQ answers from your own data — and tells you when it can’t.',
                })}
          </SheetDescription>
          {memory.available ? (
            <div className="pt-1" data-testid="pq-memory">
              <button
                type="button"
                onClick={() => setMemoryOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                data-testid="pq-memory-toggle"
              >
                <Bookmark size={12} className="text-primary" />
                {t('pqchat.memory_label', {
                  defaultValue: 'PQ remembers {{count}}',
                  count: memory.rows.length,
                })}
                {memoryOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              {memoryOpen ? (
                <div className="mt-1.5 max-h-48 overflow-y-auto space-y-1.5 rounded border border-border bg-[hsl(var(--muted))]/40 p-2">
                  {memory.rows.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">
                      {t('pqchat.memory_empty', {
                        defaultValue:
                          'Nothing yet. When you settle something ("the doors are final at 16x8 and 12x14"), PQ saves it here and stops asking.',
                      })}
                    </div>
                  ) : (
                    memory.rows.map((r) => (
                      <div key={r.id} className="flex items-start gap-2 text-[11px]">
                        <div className="min-w-0 flex-1">
                          <div className="text-foreground">{r.fact}</div>
                          <div className="text-muted-foreground">
                            {r.scope === 'company'
                              ? t('pqchat.memory_scope_company', { defaultValue: 'Company-wide' })
                              : t('pqchat.memory_scope_record', { defaultValue: 'This record' })}
                            {' · '}
                            {String(r.created_at || '').slice(0, 10)}
                            {r.said ? <span className="italic"> · “{r.said}”</span> : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleForget(r)}
                          className="shrink-0 p-0.5 text-muted-foreground hover:text-red-500"
                          title={t('pqchat.memory_forget', { defaultValue: 'Forget this' })}
                          aria-label={t('pqchat.memory_forget', { defaultValue: 'Forget this' })}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" />
              {t('pqchat.loading', { defaultValue: 'Loading conversation…' })}
            </div>
          ) : messages.length === 0 ? (
            <div className="space-y-3 pt-2">
              <div className="text-sm text-muted-foreground leading-relaxed">
                {t('pqchat.empty', {
                  defaultValue:
                    'Ask PQ about this estimate — or about your numbers: what you last paid for a material, where a job stands financially, spend by store, rebates waiting. PQ answers from your own receipts and records, cites store + date on prices, and flags anything it can’t back up.',
                })}
              </div>
              {context.type === 'estimate' ? (
                <button
                  type="button"
                  onClick={() =>
                    updateDraft(
                      t('pqchat.takeoff_seed', {
                        defaultValue:
                          'Generate a material takeoff from the walkthrough plans and photos — quantities from the printed dimensions, priced from my purchase history where you have it.',
                      })
                    )
                  }
                  className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary hover:bg-primary/10 uppercase tracking-[0.14em]"
                  data-testid="pq-chat-takeoff-chip"
                >
                  {t('pqchat.takeoff_chip', { defaultValue: '⚡ Generate a takeoff' })}
                </button>
              ) : null}
            </div>
          ) : (
            messages.map((m) => (
              <React.Fragment key={m.id}>
                <Bubble role={m.role}>{m.content}</Bubble>
                {Array.isArray(m.proposals)
                  ? m.proposals.map((p) =>
                      // A whole-document draft reviews as one card, not as
                      // one card per line (see DraftReviewCard).
                      p.kind === 'draft' ? (
                        <DraftReviewCard
                          key={p.id}
                          draft={p}
                          state={stateFor(p)}
                          onApply={handleApply}
                          onDismiss={handleDismiss}
                          canApply={!!onApplyProposal}
                          t={t}
                        />
                      ) : p.kind === 'report' ? (
                        // A standalone server-graded report card
                        // (grade_estimate). Read-only: nothing to apply, so
                        // no buttons and no place in the Apply-all sweep.
                        <div key={p.id} className="mr-auto w-full">
                          <DraftReportCard report={p.report} t={t} />
                        </div>
                      ) : (
                        <ProposalCard
                          key={p.id}
                          proposal={p}
                          state={stateFor(p)}
                          onApply={handleApply}
                          onDismiss={handleDismiss}
                          canApply={!!onApplyProposal}
                          t={t}
                        />
                      )
                    )
                  : null}
              </React.Fragment>
            ))
          )}
          {onApplyProposal && pendingProposals.length >= 3 ? (
            <Button
              size="sm"
              onClick={handleApplyAll}
              className="mr-auto h-8 bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-[0.14em]"
              data-testid="pq-chat-apply-all"
            >
              <Check size={13} className="mr-1.5" />
              {t('pqchat.apply_all', {
                defaultValue: 'Apply all {{count}}',
                count: pendingProposals.length,
              })}
            </Button>
          ) : null}
          {sending ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 size={12} className="animate-spin" />
              {t('pqchat.thinking', { defaultValue: 'PQ is thinking…' })}
            </div>
          ) : null}
          {error ? (
            <div className="text-xs text-red-500 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2">
              {error}
            </div>
          ) : null}
        </div>

        <div className="border-t border-border p-3 space-y-2">
          {/* Attachment tray — the visible answer to "did it attach?". */}
          {attachments.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="pq-chat-attachments">
              {attachments.map((a) => (
                <div
                  key={a.id}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[11px]',
                    a.status === 'failed'
                      ? 'border-red-500/50 bg-red-500/10 text-red-500'
                      : 'border-border bg-[hsl(var(--muted))] text-foreground'
                  )}
                >
                  {a.previewUrl ? (
                    <img src={a.previewUrl} alt="" className="h-8 w-8 rounded object-cover" />
                  ) : a.kind === 'pdf' ? (
                    <FileText size={14} className="text-primary shrink-0" />
                  ) : (
                    <FileSpreadsheet size={14} className="text-primary shrink-0" />
                  )}
                  <span className="max-w-[120px] truncate">{a.name}</span>
                  {a.status === 'uploading' ? (
                    <Loader2 size={11} className="animate-spin text-muted-foreground shrink-0" />
                  ) : a.status === 'attached' ? (
                    <Check size={11} className="text-green-600 shrink-0" />
                  ) : (
                    <X size={11} className="shrink-0" />
                  )}
                </div>
              ))}
              <span className="text-[10px] text-muted-foreground">
                {attachments.some((a) => a.status === 'uploading')
                  ? t('pqchat.attach_tray_uploading', { defaultValue: 'Attaching…' })
                  : attachments.some((a) => a.status === 'failed')
                    ? t('pqchat.attach_tray_failed', {
                        defaultValue: 'Some failed — re-attach the ones marked ✕',
                      })
                    : t('pqchat.attach_tray_done', {
                        defaultValue: 'Attached — PQ can see these',
                      })}
              </span>
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            {context.type === 'estimate' ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,application/pdf,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  multiple
                  hidden
                  onChange={(e) => handleAttachFiles(e.target.files)}
                  data-testid="pq-chat-attach-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attaching || sending}
                  className="h-9 px-2 border-border text-muted-foreground hover:text-foreground shrink-0"
                  title={t('pqchat.attach_title', {
                    defaultValue:
                      'Add a photo or PDF for PQ to read — or a priced supplier sheet (.xlsx) to import',
                  })}
                  data-testid="pq-chat-attach-button"
                >
                  {attaching ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Paperclip size={14} />
                  )}
                </Button>
              </>
            ) : null}
            <Textarea
              value={draft}
              onChange={(e) => updateDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={(e) => {
                // Pasting a screenshot/file attaches it, like dropping it.
                const pasted = e.clipboardData?.files;
                if (pasted && pasted.length > 0) {
                  e.preventDefault();
                  handleAttachFiles(pasted);
                }
              }}
              rows={2}
              placeholder={t('pqchat.placeholder', { defaultValue: 'Ask PQ…' })}
              className="flex-1 resize-none bg-[hsl(var(--muted))] border-border text-foreground text-sm"
              data-testid="pq-chat-input"
            />
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending || !draft.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-9"
              data-testid="pq-chat-send"
            >
              {sending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
            </Button>
          </div>
          {/* Only the most recent PQ_HISTORY_WINDOW messages are sent to PQ.
              Past that, the thread is quietly forgetting its own beginning —
              which reads as PQ "losing the plot" unless we say so. The
              estimate, coverage and price book are re-read every message and
              are NOT affected, so the notice is specifically about chat
              history. */}
          {messages.length > PQ_HISTORY_WINDOW ? (
            <div
              className="text-[11px] leading-snug text-muted-foreground border-l-2 border-[hsl(var(--warning))]/50 pl-2"
              data-testid="pq-chat-window-notice"
            >
              {t('pqchat.window_notice', {
                defaultValue:
                  'This thread has {{total}} messages — PQ only sees the last {{window}}. Earlier detail is out of its view (your estimate and price book are not affected).',
                total: messages.length,
                window: PQ_HISTORY_WINDOW,
              })}
            </div>
          ) : null}
          {messages.length > 0 ? (
            <button
              type="button"
              onClick={handleNewConversation}
              className="flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
              data-testid="pq-chat-new-conversation"
            >
              <RotateCcw size={11} />
              {t('pqchat.new_conversation', { defaultValue: 'New conversation' })}
            </button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PQChatPanel;
